import { defineMessages, type IntlShape } from 'react-intl';

import { formatProduct } from './formatProduct.ts';
import { getDescription } from './getDescription.ts';
import type { EnhancedSmartLink, LinkDirection } from './smartLinkTypes.ts';

const i18n = defineMessages({
	descriptionSeparator: {
		id: 'byline-connections.menu.item.description-separator',
		defaultMessage: '{left} • {right}',
		description:
			'Joins two parts of a connections menu item secondary line with a bullet separator (e.g. resource type, who, and when).',
	},
	productLink: {
		id: 'byline-connections.menu.item.product-link',
		defaultMessage: '{product} link',
		description:
			'Fallback title for an incoming connections menu item that has no human-readable title, naming the source product (e.g. "Slack link", "Google Docs link").',
	},
});

export type RowContent = {
	titleContent: string | undefined;
	descriptionContent: string | undefined | null;
};

const getNonGrantedRowContent = (rawUrl: string): RowContent => {
	let parsed: URL;
	try {
		parsed = new URL(rawUrl);
	} catch {
		return { titleContent: rawUrl, descriptionContent: null };
	}

	const hostname = parsed.hostname.replace(/^www\./i, '');
	// Only trim slashes from the pathname itself — slashes inside the query
	// or fragment are meaningful and must be preserved.
	const trimmedPath = parsed.pathname.replace(/^\/+|\/+$/g, '');
	const pathDisplay = `${trimmedPath}${parsed.search}${parsed.hash}` || hostname;

	return { titleContent: pathDisplay, descriptionContent: hostname };
};

const getGrantedRowContent = (
	item: EnhancedSmartLink,
	locale: string,
	formatMessage: IntlShape['formatMessage'],
): RowContent => {
	const parts = getDescription(item, locale).filter((slot): slot is string => Boolean(slot));
	const descriptionContent =
		parts.length === 0
			? null
			: parts.reduce((left, right) => formatMessage(i18n.descriptionSeparator, { left, right }));
	return { titleContent: item.title, descriptionContent };
};

const getIncomingRowContent = (
	item: EnhancedSmartLink,
	formatMessage: IntlShape['formatMessage'],
): RowContent => {
	const fallback = getNonGrantedRowContent(item.url);
	if (item.title) {
		return { ...fallback, titleContent: item.title };
	}
	const product = formatProduct(item.product);
	if (product) {
		return {
			...fallback,
			titleContent: formatMessage(i18n.productLink, { product }),
		};
	}
	return fallback;
};

export type GetConnectionRowContentOptions = {
	item: EnhancedSmartLink;
	linkDirection: LinkDirection;
	locale: string;
	formatMessage: IntlShape['formatMessage'];
};

export const getConnectionRowContent = ({
	item,
	linkDirection,
	locale,
	formatMessage,
}: GetConnectionRowContentOptions): RowContent => {
	if (item.access === 'granted') {
		return getGrantedRowContent(item, locale, formatMessage);
	}
	if (linkDirection === 'incoming') {
		return getIncomingRowContent(item, formatMessage);
	}
	return getNonGrantedRowContent(item.url);
};
